<script setup>
import { reactive, ref, toValue, onMounted, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import * as z from 'zod'
import { useApi, useApiResource } from 'soykajs/composables/api'
import { transformer } from '@/lib/transformer'
import { scrollToFirstInvalidField } from '@/lib/dom'
import { useUserStore } from '@/stores/user'
import BlockScreen from '@/components/blockscreen.vue'
import Layout from './layout.vue'

const { t } = useI18n()

const model = reactive({ email_notifications: null, fio: null, roles: [] })
const userStore = useUserStore()
const { user } = storeToRefs(userStore)
const loading = ref(false)

onMounted(() => {
  if (user.value) {
    model.email_notifications = user.value.email_notifications
    model.fio = user.value.fio
    model.roles = user.value.roles || []
  }
})

const { refresh: logoutApi } = useApi({
  url: 'logout',
  method: 'get',
})

const transform = transformer(['email_notifications', 'fio', ['roles', 'ids']])
const { refresh } = useApi({ model, transform })
const requestApi = useApiResource('current_user', model, refresh)

const validation = z.object({
  fio: z.string().nonempty(),
  email_notifications: z.email().nonempty(),
})
const validationErrors = reactive({})

async function saveUser() {
  Object.keys(validationErrors).forEach(k => delete validationErrors[k])
  try {
    validation.parse(toValue(model))
  } catch (e) {
    Object.assign(validationErrors, z.flattenError(e).fieldErrors)
    await nextTick()
    scrollToFirstInvalidField()
    return
  }

  loading.value = true
  try {
    await requestApi('put')
    await userStore.fetchCurrent()
  } catch (e) {
    loading.value = false
    throw e
  }
}

async function logout() {
  if (user.value.sso) {
    const { refresh: getLogoutLink, model: logoutLink } = useApi({ url: 'logout' })
    await getLogoutLink()
    window.location.assign(logoutLink.value.logout_uri)
  } else {
    logoutApi()
  }
  user.value = undefined
}
</script>
<template>
  <Teleport
    to="body"
    v-if="loading"
  >
    <BlockScreen />
  </Teleport>
  <Layout>
    <h1 class="text-center">{{ t('introTitle') }}</h1>
    <form @submit.prevent="saveUser">
      <div class="mb-3">
        <label class="form-label">{{ t('common.fio') }}</label>
        <input
          type="text"
          name="fio"
          class="form-control"
          :class="{ 'is-invalid': validationErrors.fio }"
          v-model="model.fio"
        />
        <div class="invalid-feedback">{{ t('errors.fioRequired') }}</div>
      </div>
      <div class="mb-3">
        <label class="form-label">{{ t('common.email') }}</label>
        <input
          type="text"
          name="email_notifications"
          class="form-control"
          :class="{ 'is-invalid': validationErrors.email_notifications }"
          v-model="model.email_notifications"
        />
        <div class="invalid-feedback">{{ t('errors.emailInvalid') }}</div>
      </div>
      <button
        type="submit"
        class="btn btn-primary"
      >
        {{ t('saveContinue') }}
      </button>
      <button
        @click.prevent="logout"
        class="btn btn-outline-danger logout-button"
      >
        {{ t('logout') }}
      </button>
    </form>
  </Layout>
</template>

<i18n>
{
  "ru": {
    "introTitle": "Представьтесь, пожалуйста",
    "errors": {
      "fioRequired": "Нужно заполнить",
      "emailInvalid": "Нужен правильный адрес эл. почты"
    },
    "saveContinue": "Сохранить и продолжить",
    "logout": "Выйти"
  },
  "en": {
    "introTitle": "Please introduce yourself",
    "errors": {
      "fioRequired": "This field is required",
      "emailInvalid": "Enter a valid email address"
    },
    "saveContinue": "Save and continue",
    "logout": "Sign out"
  }
}
</i18n>
